/**
 * Primitives for the source-review workspace shared between Neptune (320px
 * sliding panel) and Venus (50% pane in the agent two-pane layout).
 *
 * Each primitive is intentionally small so each surface can compose its own
 * container (Card vs Collapsible vs plain div) without forcing a shared layout.
 */
import * as React from "react";
import { Check, ChevronDown, HelpCircle, MessagesSquare, User, X } from "lucide-react";
import { useTranslation } from "react-i18next";
// #433 AI-fact marker, extracted to ui/ so the AI Button variant shares it (#455).
import type { PastCaseView } from "../types/assistant";
import { Button } from "./v2/ui/button";
import { CONFIRM_CHANGES_TUTORIAL_KEY, ConfirmChangesTutorial } from "./ConfirmChangesTutorial";
import { useSeenOnce } from "../hooks/useSeenOnce";
import { cn } from "../lib/utils";
import { ConfirmBurst } from "./ConfirmBurst";
import {
  ConversationSummaryCard as V2ConversationSummaryCard,
  CustomerRequestCard as V2CustomerRequestCard,
  GeneralNotesSection as V2GeneralNotesSection,
} from "./v2/CustomerRequestCard";
import { GradientSparkle } from "./ui/gradient-sparkle";
import { factMachineScope, type FactSourceRef } from "../lib/factSourceReferenceIds";
import { MachineScopeMarker } from "./MachineScopeMarker";
import { GroundednessBadge } from "./request-cards";

/** Idle gap after the last keystroke before a fact auto-commits (and thus
 *  schedules a regeneration + lights the spinner). Kept long enough that a
 *  normal mid-sentence pause does not count as "fact finished" — Enter / blur
 *  still flush immediately for the explicit "done" gesture. At 1.5 s a pause
 *  after the first word saved "GEberleitungen" and the answer was written from
 *  that fragment (#826). Exported so tests drive the debounce off the real
 *  value instead of restating the number. */
export const COMMIT_DEBOUNCE_MS = 5000;

// ─── To-Do list ────────────────────────────────────────────────────────

/**
 * Provenance-aware groundedness verdict for a to-do's seed content (mirrors the
 * Jupiter `CustomerRequestGroundedness` enum). GROUNDED/PARTIAL/UNGROUNDED are the AI-content
 * verdicts the seed verifier emits; HUMAN_PROVIDED marks human-authored content
 * as authoritative. Kept as a wide string union so a server `CustomerRequest` (whose
 * generated enum is a nominal string-literal type) stays structurally assignable
 * without a cast, and an unknown future verdict never breaks the badge.
 */
export type CustomerRequestGroundedness =
  | "GROUNDED"
  | "PARTIAL"
  | "UNGROUNDED"
  | "HUMAN_PROVIDED";

/**
 * One per-claim citation on a to-do's seed (mirrors the Jupiter `CustomerRequestCitation`
 * shape). `num` is the seed-pool source number; `ragQuestionId` is the to-do the
 * source was attributed to (null = a shared "Weitere Quellen" source).
 */
export interface CustomerRequestCitation {
  num: number;
  // Nullable to match the GraphQL contract (nullable scalars generate `| null`).
  title?: string | null;
  quoteStart?: string | null;
  sourceId?: string | null;
  // The cited chunk's Qdrant id, which is what a fact's provenance matches on to
  // highlight the one card it was written from (factSourceReferenceIds tier 1).
  qdrantSourceId?: string | null;
  sourceType?: string | null;
  ragQuestionId?: string | null;
  // Display fields the to-do source card renders + opens from. The to-do shows
  // its sources straight from these citations (there is no pooled MessageSource
  // row for a prefill-cited source), so the citation carries what the shared
  // source-card renderer needs: excerpt for the preview, document id/page to open
  // a document, and the source message id to open a past email.
  excerpt?: string | null;
  sourceDocumentId?: string | null;
  sourceDocumentPageNumber?: number | null;
  sourceConversationMessageId?: string | null;
  // Conversation the cited source belongs to, so a Message-type source (a
  // similar service case) can be opened: in-app via sourceConversationId, or as
  // an external ticket via sourceConversationExternalId (e.g. Advasco). Resolved
  // server-side from sourceConversationMessageId.
  sourceConversationId?: string | null;
  sourceConversationExternalId?: string | null;
  // Contact a memory (Kundenhistorie) citation belongs to, so the source card
  // opens the contact's CRM entry. Null for non-memory sources.
  sourceContactContextId?: string | null;
  // DB-row identity of a database citation, so the source card can fetch and
  // render the live row (same contract as MessageSource). Null for
  // non-database sources.
  sourceDatabaseTableId?: string | null;
  sourceDatabaseRowPkValues?: string | null;
  pastCase?: PastCaseView | null;
  sourceDatabaseColumnName?: string | null;
  // Machine scope: `scopeExpectedLabel` (the type the request asked about) is set
  // only for a source the retrieval scope engine judged off-type, so it doubles as
  // the "similar machine type" marker; `scopeSourceLabel` names the type the source
  // is about, and is null when it covers too many to name.
  scopeSourceLabel?: string | null;
  scopeExpectedLabel?: string | null;
  // A violated cross-cutting fact (control platform), independent of the machine type.
  scopeFactLabel?: string | null;
  // ISO date the cited CONTENT is from (a document's authored date, a past case's
  // send date) — not when it was ingested. Null for undated content.
  contentDate?: string | null;
}

/**
 * A single extracted (or human-added) to-do.
 *
 * Mirrors the Jupiter `customerRequests` GraphQL contract. Every to-do renders
 * through one card: the generation fields are all optional/nullable, and a
 * null seed is expected until generation lands, so the card degrades to a
 * blank-but-functional shell.
 */
export interface CustomerRequest {
  /** Stable identity; sources are grouped by `customerRequestId === customerRequest.id`. */
  id: string;
  text: string;

  // ─── Prefill seeds (all nullable; null → the card renders nothing) ──
  /** The raw record a lookup resolved against; its presence selects the
   *  lookup/decision card. */
  /** Short reasoning rendered with the explanation intro. */
  rationale?: string | null;

  /** Provenance-aware groundedness verdict for this to-do's seed content, set by
   *  the (feature-flagged) seed verifier. Null when the verifier did not run.
   *  Drives the per-card groundedness badge. */
  groundedness?: CustomerRequestGroundedness | string | null;
  /** True when the AI prose was dropped because the agent curated a fact it was
   *  written from. The Erklärung shows a "suggestion outdated" note instead of
   *  the no-sources empty state; a fresh prose returns with the next full
   *  generation. */
  /** Per-claim citations: the numbered sources the seed drew concrete claims
   *  from (own or shared). Null/empty → no citation affordance. */
  citations?: CustomerRequestCitation[] | null;
}

interface CustomerRequestsListProps {
  customerRequests: CustomerRequest[];
  className?: string;
  /**
   * Host-provided callback fired when the agent clicks a to-do's grounding
   * source or a numbered citation. The host reveals the source's content via its
   * EXISTING references-panel path (open + scroll-to + highlight) — the shared
   * package owns no content-fetch/open logic. The argument is the source's
   * reference id (`CardSource.id` / `CustomerRequestCitation.sourceId`), the same id the
   * host's reference cards highlight by. Optional: when absent the source rows
   * render as non-clickable text (degrade, no throw).
   */
  /**
   * Per-to-do agent-facts binding. When provided, each card renders its own
   * "User Facts" section bound to that card's `customerRequest.id`. The host owns the
   * SINGLE `useFactsNotebook` instance per message and passes scoped reads
   * + scoped add/remove/edit down here — there is deliberately no second hook
   * instance per card (that would race the shared Apollo cache field). Absent →
   * no per-to-do section renders (back-compat). See {@link CustomerRequestFacts}.
   */
  facts?: CustomerRequestFacts;
  /**
   * Host-provided renderer for a to-do's grounding-source panel, shown inside
   * the card (typically the {@link CustomerRequestSourceReferences} disclosure wrapping
   * {@link MessageReferences}). Both hosts provide it; it is the only source
   * rendering the card has. Returns null/undefined for a to-do with no sources
   * (the renderer then contributes nothing).
   *
   * `intro`, when passed, is the AI's explanation (drafted answer / found info
   * + rationale) for why these sources back the request — the host renders it
   * as plain introduction text at the top of its sources panel, inside the
   * same disclosure, instead of a separate "Erklärung" expander.
   */
  renderCustomerRequestSources?: (
    customerRequestId: string,
    intro?: React.ReactNode,
  ) => React.ReactNode;
  /**
   * Per-request dirty flag. When provided, every card ends in a green confirm
   * button: enabled while the request's facts changed since the agent last
   * confirmed them (`dirtyIds` holds that request's id), disabled otherwise.
   * Absent → no button renders (Venus does not use it).
   */
  confirmation?: CustomerRequestConfirmation;
  /** The card design. Default `"v1"`. */
  appearance?: SourceReviewAppearance;
}

/**
 * Which design the source-review cards use. `"v1"` is the original look and the default, so a host
 * that has not migrated (Neptune) is untouched; `"v2"` renders the design-library cards. A
 * transitional switch: it goes when every host is on v2.
 */
export type SourceReviewAppearance = "v1" | "v2";

export interface CustomerRequestConfirmation {
  /** Ids of the customer requests whose facts changed and are not yet confirmed. */
  dirtyIds: ReadonlySet<string>;
  onConfirm: (customerRequestId: string) => void;
  /** Takes a confirmation back: the request is held again until confirmed anew. */
  onUnconfirm: (customerRequestId: string) => void;
  /** The request whose button the first-time pointer attaches to, if any. */
  tutorialRequestId?: string;
}

/** The confirm control of the ticket-level notes; the host passes none while there is nothing to confirm. */
export interface GeneralNotesConfirmation {
  dirty: boolean;
  onConfirm: () => void;
  onUnconfirm: () => void;
  /** The first-time pointer attaches to the notes' button. */
  tutorial?: boolean;
}

function ConfirmChangesButton({
  dirty,
  onConfirm,
  onUnconfirm,
  tutorial = false,
}: {
  dirty: boolean;
  onConfirm: () => void;
  onUnconfirm: () => void;
  /** This is the button the one-time pointer attaches to. */
  tutorial?: boolean;
}) {
  const { t } = useTranslation();
  const { seen, markSeen } = useSeenOnce(CONFIRM_CHANGES_TUTORIAL_KEY);
  // The spring and the burst play for a confirmation made here, just now, and run on their own
  // short clock. A card that is already confirmed when the page loads does not animate.
  const [celebrating, setCelebrating] = React.useState(false);
  React.useEffect(() => {
    if (!celebrating) return;
    const timer = setTimeout(() => setCelebrating(false), 1100);
    return () => clearTimeout(timer);
  }, [celebrating]);
  const pointing = tutorial && !seen;

  // One control, two states, both green: the filled green action while unconfirmed, the
  // green outline with a check once confirmed. Pressing a confirmed one takes it back. A press
  // is a local write, so it answers at once: there is no server to wait for.
  const confirmButton = (
    <Button
      size="sm"
      variant="success"
      // While the pointer is open the button breathes a soft ring, so the eye finds it.
      className={pointing ? "motion-safe:animate-confirm-pulse" : undefined}
      onClick={() => {
        // Pressing the button it points at is the lesson; the pointer is done.
        markSeen();
        setCelebrating(true);
        onConfirm();
      }}
    >
      {t("sourceReview.confirmChanges", "Bestätigen")}
    </Button>
  );
  return (
    <div className="mt-3 flex justify-end">
      {dirty ? (
        tutorial ? (
          <ConfirmChangesTutorial open={!seen} onDismiss={markSeen}>
            {confirmButton}
          </ConfirmChangesTutorial>
        ) : (
          confirmButton
        )
      ) : (
        <span className="relative inline-flex">
          <Button
            size="sm"
            variant="outline"
            leadingIcon={Check}
            aria-pressed
            title={t("sourceReview.unconfirmChanges", "Bestätigung zurücknehmen")}
            className={cn("border-success text-success-strong", celebrating && "motion-safe:animate-confirm-pop")}
            onClick={() => {
              setCelebrating(false);
              onUnconfirm();
            }}
          >
            {t("sourceReview.confirmedChanges", "Bestätigt")}
          </Button>
          {celebrating && <ConfirmBurst />}
        </span>
      )}
    </div>
  );
}

/**
 * The host's single-owner agent-facts notebook, exposed to `CustomerRequestsList` so each
 * card can render its OWN per-to-do facts section without spinning up a second
 * hook instance. All callbacks operate against the host's one
 * `useFactsNotebook` instance; `factsFor(customerRequestId)` returns just that to-do's
 * facts, and `onAdd`/`onAddAfter` are scoped to the to-do (the host binds the
 * `customerRequestId` so the card never has to). This is what keeps the per-card sections
 * race-free: one writer, one cache path, scoped reads.
 */
export interface CustomerRequestFacts {
  /** Facts scoped to one to-do (customerRequestId = `customerRequest.id`). */
  factsFor: (customerRequestId: string) => ReadonlyArray<MessageFact>;
  /** Add a fact scoped to one to-do. Returns the notebook add handle. */
  onAdd: (
    customerRequestId: string,
    content: string,
  ) => { tempId: string; settled: Promise<{ realId: string | null }> };
  /** Add a fact anchored after `afterId`, scoped to one to-do. */
  onAddAfter: (
    customerRequestId: string,
    afterId: string,
    content: string,
  ) => { tempId: string; settled: Promise<{ realId: string | null }> };
  onRemove: (id: string) => void;
  onEdit?: (id: string, content: string) => void | Promise<void>;
  /** Fired on every keystroke, before the debounced commit on blur/Enter. */
  onDraftChange?: () => void;
  /** Live count (0/1) of typed-but-uncommitted drafts per to-do, so the host can
   *  treat typed input as a fact right away. */
  onPendingCountChange?: (customerRequestId: string, count: number) => void;
  /** Visual deselect — clicking the ✓ checkbox dismisses the fact (line-through)
   *  WITHOUT removing it. Without this, the card's checkbox falls back to
   *  onRemove and deselecting deletes the fact — destructive, and wrong for the
   *  AI-prefilled bullets that live in these cards. Pair with `dismissedFactIds`. */
  onToggle?: (id: string) => void;
  /** IDs of facts to render as deselected. Pair with `onToggle`. */
  dismissedFactIds?: Set<string>;
  /** Subset of `dismissedFactIds` the AI offered deselected (#706): rendered
   *  as a suggestion, without the line-through of an agent's rejection. */
  suggestedFactIds?: Set<string>;
  onReferenceHover?: (id: string | null) => void;
  /** #500: fires with a fact's id on row hover (`null` on leave) so the host can
   *  highlight that fact's grounding sources. The AI-prefilled bullets live in
   *  these per-to-do sections, so this is the path that carries the feature. */
  onFactHover?: (factId: string | null) => void;
  /** Ids of facts to highlight because the pointer is on a source they were
   *  drawn from. */
  highlightedFactIds?: Set<string> | null;
}

/**
 * The per-to-do "User Facts" section a card renders below its read-only
 * found-information. Reuses the same {@link FactsNotebook} as the general
 * workspace-level section so the two look identical; the only difference is the
 * scope: every add/remove/edit here is bound to this card's `customerRequestId` via the
 * host's single notebook instance (no second hook → no cache race).
 */
function CustomerRequestFactsSection({
  customerRequestId,
  facts,
  hideDivider = false,
}: {
  customerRequestId: string;
  facts: CustomerRequestFacts;
  /** No hairline above the facts: the card already separates them from its title (v2). */
  hideDivider?: boolean;
}) {
  const { onPendingCountChange } = facts;
  const handlePendingCount = React.useCallback(
    (count: number) => onPendingCountChange?.(customerRequestId, count),
    [onPendingCountChange, customerRequestId],
  );
  return (
    <FactsNotebook
      onPendingCountChange={handlePendingCount}
      facts={facts.factsFor(customerRequestId)}
      onAdd={(text) => facts.onAdd(customerRequestId, text)}
      onAddAfter={(afterId, text) => facts.onAddAfter(customerRequestId, afterId, text)}
      onRemove={facts.onRemove}
      onToggle={facts.onToggle}
      dismissedFactIds={facts.dismissedFactIds}
      suggestedFactIds={facts.suggestedFactIds}
      onEdit={facts.onEdit}
      onDraftChange={facts.onDraftChange}
      onReferenceHover={facts.onReferenceHover}
      onFactHover={facts.onFactHover}
      highlightedFactIds={facts.highlightedFactIds}
      hideDivider={hideDivider}
      className={hideDivider ? undefined : "mt-1.5"}
    />
  );
}

/**
 * Whether a to-do actually has explanation content to render inside the sources
 * panel — i.e. whether its card body or its reasoning would put anything on
 * screen.
 */
function customerRequestHasExplanation(customerRequest: CustomerRequest): boolean {
  const hasReasoning = (customerRequest.rationale?.trim().length ?? 0) > 0;
  // The card renders only the groundedness badge (when the verifier ran);
  // without it and without reasoning the card body is empty.
  return hasReasoning || !!customerRequest.groundedness;
}

/**
 * Per-card resilience boundary. An archetype card must never be able to blank
 * the whole to-dos list: a throw during its render would otherwise unmount the
 * entire CustomerRequestsList subtree. Catching it here degrades only the affected card to
 * its read-only fallback row; every other to-do keeps rendering.
 */
class CustomerRequestCardBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("CustomerRequestCardBoundary caught a to-do card error:", error, info);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

interface CustomerRequestCardProps {
  customerRequest: CustomerRequest;
  /** The head glyph (question vs task), already sized + tinted by the caller. */
  glyph: React.ReactNode;
  /** The card body (facts, sources, archetype readout), or nothing. */
  children?: React.ReactNode;
}

/**
 * The shared card surface for a single to-do. White surface, large radius, a
 * soft navy-tinted shadow that defines the edge (a faint hairline sits under
 * it), generous padding, and a clickable head that collapses the body (default
 * expanded). All transitions respect `prefers-reduced-motion`.
 */
function CustomerRequestCard({ customerRequest, glyph, children }: CustomerRequestCardProps) {
  const [collapsed, setCollapsed] = React.useState(false);
  const hasHeading = customerRequest.text.trim().length > 0;
  const hasBody = !!children;

  return (
    <li className="overflow-hidden rounded-2xl border border-muted bg-card shadow-[0_1px_3px_rgba(7,25,95,0.08),0_1px_2px_rgba(7,25,95,0.05)] motion-safe:transition-shadow hover:shadow-[0_4px_12px_rgba(7,25,95,0.10),0_2px_4px_rgba(7,25,95,0.06)]">
      {(() => {
        const head = (
          <>
            <span>{glyph}</span>
            <span className="flex-1">
              {hasHeading && (
                <span className="block text-[13px] font-semibold leading-snug text-ink">
                  {customerRequest.text}
                </span>
              )}
            </span>
          </>
        );
        // Only a card with a body gets a collapsible head button; a bodyless
        // row renders a plain, non-interactive head so it carries no controls.
        return hasBody ? (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-expanded={!collapsed}
            className="flex w-full items-center gap-2.5 px-3.5 py-3 text-left"
          >
            {head}
          </button>
        ) : (
          <div className="flex w-full items-center gap-2.5 px-3.5 py-3">{head}</div>
        );
      })()}
      {hasBody && !collapsed && (
        // Body aligns under the head padding (not indented under the heading text):
        // the panel is narrow, so the body — and the full-width Eigene-Informationen
        // notebook inside it — uses the same horizontal padding as the head.
        <div className="px-3.5 pb-3.5">{children}</div>
      )}
    </li>
  );
}

/**
 * The same card surface {@link CustomerRequestCard} uses, without a head — for the general
 * section, which is a to-do card minus the customer ask: one box holding the
 * sources that belong to no request plus the ticket-level notes. Its headline is
 * rendered by the host ABOVE the box, matching the "Kundenanliegen" header above
 * the to-do list, so the box itself carries no title.
 */
export function GeneralSectionCard({
  children,
  className = "",
}: {
  children?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-muted border-t-0 bg-card px-3.5 py-3.5 shadow-[0_1px_3px_rgba(7,25,95,0.08),0_1px_2px_rgba(7,25,95,0.05)] motion-safe:transition-shadow hover:shadow-[0_4px_12px_rgba(7,25,95,0.10),0_2px_4px_rgba(7,25,95,0.06)] ${className}`}
    >
      {children}
    </div>
  );
}

export function CustomerRequestsList({
  customerRequests,
  className,
  facts,
  renderCustomerRequestSources,
  confirmation,
  appearance = "v1",
}: CustomerRequestsListProps) {
  const { t } = useTranslation();
  if (customerRequests.length === 0) {
    return (
      <p className={`text-sm text-muted-foreground${className ? " " + className : ""}`}>
        {t("sourceReview.customerRequestEmpty", "Keine Kundenanliegen erkannt")}
      </p>
    );
  }

  // The glyph anchors the card head. There is no per-to-do action/question
  // distinction left in the payload, so every card gets the same neutral
  // question-mark glyph.
  const customerRequestGlyph = (): React.ReactNode => (
    <HelpCircle aria-hidden className="h-6 w-6 shrink-0 text-ink-faint" />
  );

  // When the host injects a full per-to-do source panel, the card's lightweight
  // per-claim citation disclosure is suppressed so the agent sees one (superior)
  // source affordance, not two.
  const injectSources = !!renderCustomerRequestSources;

  // The fact-led card layout: the agent's concise bullet facts lead the card
  // surface; the AI's verbose drafted answer / found info and its reasoning
  // become the intro text of the sources panel below ("Erklärung"), so the
  // long-form prose stays subordinate to the bullets the agent works from.
  const cardRow = (
    customerRequest: CustomerRequest,
    card: React.ReactNode,
    glyph: React.ReactNode,
  ) => {
    const explanationHasContent = customerRequestHasExplanation(customerRequest);
    // The explanation (found info / rationale) renders as the intro text of
    // the host's sources panel, read together with the sources it explains.
    const hasRationale = !!customerRequest.rationale?.trim();
    const explanationIntro = explanationHasContent || hasRationale ? (
      <div className="space-y-2">
        {card}
        {hasRationale && (
          <p className="text-xs leading-relaxed text-ink">{customerRequest.rationale}</p>
        )}
      </div>
    ) : // No "keine Dokumente gefunden" line: it only ever made sense for a
    // to-do retrieval actually ran for, which was the lookup shape, and that
    // shape is gone. On this path it would be the sole content of an otherwise
    // empty "Quellen" disclosure. Null lets the host's
    // `refs.length === 0 && !intro` guard drop the disclosure entirely.
    null;
    const sourcesNode = injectSources
      ? renderCustomerRequestSources?.(customerRequest.id, explanationIntro)
      : null;
    const confirmButton =
      confirmation ? (
        <ConfirmChangesButton
          dirty={confirmation.dirtyIds.has(customerRequest.id)}
          onConfirm={() => confirmation.onConfirm(customerRequest.id)}
          onUnconfirm={() => confirmation.onUnconfirm(customerRequest.id)}
          tutorial={confirmation.tutorialRequestId === customerRequest.id}
        />
      ) : null;
    const body = (
      <>
        {facts && (
          <CustomerRequestFactsSection
            customerRequestId={customerRequest.id}
            facts={facts}
            hideDivider={appearance === "v2"}
          />
        )}
        {sourcesNode && <div className="mt-3">{sourcesNode}</div>}
        {confirmButton}
      </>
    );
    if (appearance === "v2") {
      return (
        <li key={customerRequest.id}>
          {/* Not collapsible: the facts and sources are what the agent works on here. */}
          <V2CustomerRequestCard title={customerRequest.text} collapsible={false} bodyClassName="border-t-0 pt-0">
            {body}
          </V2CustomerRequestCard>
        </li>
      );
    }
    return (
      <CustomerRequestCard key={customerRequest.id} customerRequest={customerRequest} glyph={glyph}>
        {body}
      </CustomerRequestCard>
    );
  };

  // Every to-do renders the same card: the fact list, the sources panel with
  // the rationale as its intro, and the groundedness badge when the verifier
  // ran. Keyed by the stable `customerRequest.id` so a seed refetch never remounts a card
  // (no flash, no lost focus).
  const renderCustomerRequest = (customerRequest: CustomerRequest) => {
    const glyph = customerRequestGlyph();

    // The card renders inside a resilience boundary keyed on the to-do id: a
    // throw degrades only that card to its read-only fallback row, so the
    // to-dos list never blanks.
    const guarded = (card: React.ReactNode) =>
      cardRow(
        customerRequest,
        <CustomerRequestCardBoundary fallback={<span className="flex-1">{customerRequest.text}</span>}>
          {card}
        </CustomerRequestCardBoundary>,
        glyph,
      );

    // No card body of its own — the card carries the fact list, the sources
    // panel with the rationale as its intro, and (when the verifier ran) the
    // groundedness badge.
    return guarded(
      customerRequest.groundedness ? (
        <GroundednessBadge groundedness={customerRequest.groundedness} />
      ) : null,
    );
  };

  return (
    <ul className={`flex flex-col gap-3 text-sm${className ? " " + className : ""}`}>
      {customerRequests.map(renderCustomerRequest)}
    </ul>
  );
}

// ─── Conversation summary card ─────────────────────────────────────────

interface ConversationSummaryCardProps {
  /** Rolling whole-thread summary (Message.conversationSummary). */
  summary: string | null | undefined;
  /** Collapsed state is owned by the caller so it can persist per workspace. */
  collapsed?: boolean;
  onToggle?: () => void;
  className?: string;
  /** The card design. Default `"v1"`. */
  appearance?: SourceReviewAppearance;
}

/**
 * Entry-point summary shown at the top of the agent workspace. Gives the agent
 * the gist of a long conversation (what the customer wants, what is open) before
 * they dive into the to-dos and sources. Renders nothing when there is no summary.
 */
export function ConversationSummaryCard({
  summary,
  collapsed = false,
  onToggle,
  className,
  appearance = "v1",
}: ConversationSummaryCardProps) {
  const { t } = useTranslation();
  if (!summary || !summary.trim()) {
    return null;
  }
  if (appearance === "v2") {
    return (
      <V2ConversationSummaryCard
        summary={summary}
        open={!collapsed}
        onOpenChange={onToggle ? () => onToggle() : undefined}
        labels={{ title: t("sourceReview.conversationSummary", "Zusammenfassung des Gesprächs") }}
        className={className}
      />
    );
  }
  return (
    <div
      className={`overflow-hidden rounded-2xl border border-muted bg-card shadow-[0_1px_3px_rgba(7,25,95,0.08),0_1px_2px_rgba(7,25,95,0.05)]${className ? " " + className : ""}`}
    >
      <button
        type="button"
        onClick={onToggle}
        disabled={!onToggle}
        className="flex w-full items-center gap-2 bg-info-subtle/40 px-3.5 py-2 text-left motion-safe:transition-colors disabled:cursor-default enabled:hover:bg-info-subtle/60"
        aria-expanded={!collapsed}
      >
        <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-info-subtle text-ink">
          <MessagesSquare className="h-3 w-3" strokeWidth={2} />
        </span>
        <span className="flex-1 text-xs font-semibold text-ink">
          {t("sourceReview.conversationSummary", "Zusammenfassung des Gesprächs")}
        </span>
        {onToggle && (
          <ChevronDown
            className={`h-3.5 w-3.5 shrink-0 text-ink-faint motion-safe:transition-transform motion-safe:duration-200${collapsed ? " -rotate-90" : ""}`}
          />
        )}
      </button>
      {!collapsed && (
        <p className="border-t border-muted px-4 py-3 text-sm leading-relaxed text-ink whitespace-pre-line">
          {summary}
        </p>
      )}
    </div>
  );
}

// ─── Manuell context card ──────────────────────────────────────────────

interface ManualContextCardProps {
  text: string;
}

/**
 * One green "Manuell"-tagged card representing context the support agent
 * typed into the chat while in source-review mode. The parent maintains
 * the array; this component renders one entry.
 */
export function ManualContextCard({ text }: ManualContextCardProps) {
  return (
    <div className="flex items-start gap-2 p-2 rounded-md border border-green-200 bg-green-50 text-sm">
      <span className="shrink-0 text-xs font-medium text-green-700 border border-green-300 rounded px-1 py-0.5">
        Manuell
      </span>
      <span className="text-green-900">{text}</span>
    </div>
  );
}

// ─── Agent-contributed facts ───────────────────────────────────────────

export interface MessageFact {
  id: string;
  content: string;
  createdAt?: string;
  /** Optional to-do scope this fact belongs to. null = a general per-message
   *  fact (the workspace-level "Meine Fakten" section); a value scopes the fact
   *  to that to-do's card (the to-do's `id` / rag_question_id). Consumers filter
   *  on this to split the single facts list into general vs per-to-do. */
  customerRequestId?: string | null;
  /** True for a bullet the AI prefilled into a to-do's notebook. Rendered in
   *  italics to signal its AI provenance; flips to false the moment the agent
   *  edits the row (they have adopted it). Absent → treated as false. */
  isAiGenerated?: boolean;
  /** Provenance of an AI-prefilled bullet: the sources it was grounded in at
   *  generation time (#500). Hovering the row highlights the matching source
   *  cards — see `factSourceReferenceIds`, which resolves these to card ids.
   *  Null/absent for human-typed facts (the agent IS the source) and for rows
   *  written before provenance was recorded, so consumers must treat a missing
   *  value as "no provenance to show" rather than an error. */
  sourceRefs?: readonly FactSourceRef[] | null;
  /** A key that stays the same across the temp→real id transition AND
   *  across the cleanup pass that moves a row from `pendingAdds` to
   *  `serverFacts`. Use this for React keys and for tracking which row the
   *  agent is editing — otherwise the row unmounts (losing focus) at every
   *  server confirmation. The hook always populates this; consumers can
   *  fall back to `id` if they construct facts by hand. */
  stableKey?: string;
}

interface FactCardProps {
  fact: MessageFact;
  /** Called with fact.id when the agent clicks the dismiss "x". */
  onRemove?: (id: string) => void;
  /** Optional click on the card (outside the dismiss button). */
  onClick?: (id: string) => void;
  className?: string;
}

/**
 * Single persisted "Von Ihnen" fact card. Uses a quiet deep-blue treatment to
 * mark it as agent-contributed (vs the green ManualContextCard which is
 * in-memory only).
 */
export function FactCard({
  fact,
  onRemove,
  onClick,
  className,
}: FactCardProps) {
  const { t } = useTranslation();
  return (
    <li
      role="listitem"
      aria-label={`${t("sourceReview.ownFactInfo")}: ${fact.content}`}
      onClick={() => onClick?.(fact.id)}
      className={`grid grid-cols-[16px_20px_1fr_auto] items-start gap-2 rounded-md border border-muted bg-primary-subtle/40 p-2 hover:bg-primary-subtle/60 transition-colors${className ? " " + className : ""}`}
    >
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center h-4 w-4 rounded-sm bg-primary border border-primary-accent text-primary-foreground"
      >
        <Check className="h-2.5 w-2.5" strokeWidth={3} />
      </span>
      <span
        aria-hidden="true"
        className="inline-flex items-center justify-center h-5 w-5 rounded-md bg-primary text-primary-foreground"
      >
        <User className="h-3 w-3" />
      </span>
      <div className="min-w-0">
        <div className="truncate text-sm text-foreground">{fact.content}</div>
        <div className="text-[10px] font-semibold uppercase tracking-wider text-ink-faint">
          VON IHNEN
        </div>
      </div>
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(fact.id);
          }}
          aria-label={t("sourceReview.removeFact")}
          title={t("sourceReview.remove")}
          className="inline-flex items-center justify-center h-[18px] w-[18px] rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-accent"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </li>
  );
}

interface FactsNotebookProps {
  facts: ReadonlyArray<MessageFact>;
  /** Adds a new fact and returns a synchronous tempId for follow-up edits.
   *  The notebook uses this to wire the auto-commit-on-type flow: it tracks
   *  the tempId across typing bursts and routes subsequent keystrokes to
   *  onEdit instead of producing a new fact per pause. */
  onAdd: (text: string) => {
    tempId: string;
    settled: Promise<{ realId: string | null }>;
  };
  /** Like onAdd, but the new fact's pending entry is anchored directly after
   *  the fact identified by `afterId`. Required for the Enter-on-row "split
   *  list" flow (insert directly below the edited row instead of appending
   *  to the end). When omitted, Enter inside a row just commits the edit
   *  without chaining a new row. */
  onAddAfter?: (afterId: string, text: string) => {
    tempId: string;
    settled: Promise<{ realId: string | null }>;
  };
  /** Permanent removal. The only entry point now is "edit to empty + Enter"
   *  inside the row (the hover-x has been removed). The caller typically
   *  soft-deletes the fact via `messageRemoveFact`. */
  onRemove: (id: string) => void;
  /** Persist an edit to an existing fact's content. Triggered when the user
   *  clicks a saved row, modifies the text, and confirms via Enter/blur. */
  onEdit?: (id: string, content: string) => void | Promise<void>;
  /** Fired on every keystroke in an edit/insert row, before the debounced
   *  commit on blur/Enter. Lets the parent surface a "regenerating" indicator
   *  as soon as the agent starts typing rather than only once the mutation
   *  fires. */
  onDraftChange?: () => void;
  /** Visual deselect — fired by clicking the ✓ checkbox. Pair with
   *  `dismissedFactIds`. */
  onToggle?: (id: string) => void;
  /** IDs of facts to render as deselected (line-through). Pair with
   *  `onToggle`. */
  dismissedFactIds?: Set<string>;
  /** Subset of `dismissedFactIds` the AI offered deselected (#706) and no
   *  agent has touched. Rendered faded with a brand-gradient checkbox instead
   *  of struck through: a suggestion to include, not a rejection. */
  suggestedFactIds?: Set<string>;
  isAdding?: boolean;
  /** Notifies parent of pending/in-progress fact contributions that are not
   *  yet reflected in `facts`. Sum of (locally-submitted but not yet echoed
   *  from server) + (current draft length > 0 ? 1 : 0). Lets the parent
   *  surface a live count in CTAs while the user is still typing. */
  onPendingCountChange?: (count: number) => void;
  /** Propagates badge hover events to the answer text highlight system. */
  onReferenceHover?: (id: string | null) => void;
  /** #500: fires with a fact's id while the pointer rests on its row (`null` on
   *  leave), so the host can highlight the sources that fact was grounded in.
   *  Omit to leave fact rows inert. */
  onFactHover?: (factId: string | null) => void;
  /** Ids of facts to highlight because the pointer is resting on a source they
   *  were drawn from. */
  highlightedFactIds?: Set<string> | null;
  /** Suppress the notebook's own "Eigene Angaben" header. Used at ticket level,
   *  where the host already renders an outer "Allgemeine Notizen" header — the
   *  inner header would otherwise stack a second heading directly under it. */
  hideHeader?: boolean;
  /** Keep the "Eigene Angaben" label but drop the hairline above it (the v2 cards separate it themselves). */
  hideDivider?: boolean;
  className?: string;
}

/**
 * Apple-Notes-style checklist for agent-contributed facts. One block: saved
 * facts on top (each as a row with a deep-blue ✓), an always-visible empty
 * input row at the bottom. Enter submits and keeps focus so the next fact
 * can be typed immediately. Click the ✓ or hover-x to remove a saved fact.
 */
export function FactsNotebook({
  facts,
  onAdd,
  onAddAfter,
  onRemove,
  onEdit,
  onDraftChange,
  onToggle,
  dismissedFactIds,
  suggestedFactIds,
  // isAdding is intentionally not destructured. The caller still passes its
  // mutation loading flag, but the notebook no longer locks the input or
  // shows a separate "saving" row — Apollo's optimisticResponse makes the
  // new fact appear in `facts` synchronously.
  onPendingCountChange,
  // onReferenceHover is intentionally not destructured. Callers still pass it
  // (the plumbing stays on the interface), but the reference-number badge that
  // consumed it has been removed, so fact rows no longer render any number.
  onFactHover,
  highlightedFactIds,
  hideHeader,
  hideDivider = false,
  className,
}: FactsNotebookProps) {
  // When the user presses Enter inside an existing fact row, render an
  // editable draft row directly below it so they can keep adding facts in
  // place. The draft is purely local until the user types content and
  // commits — only then does it materialize via onAddAfter, so abandoned
  // empty inserts don't leak as empty rows to the server.
  // Holds the anchor row key plus any text that should pre-fill the injected
  // draft. `initialText` is the "rest of the line" carried down when the user
  // presses Enter mid-fact — the WYSIWYG split-at-cursor behavior.
  const [insertAfterTarget, setInsertAfterTarget] = React.useState<
    { key: string; initialText: string } | null
  >(null);
  const [draft, setDraft] = React.useState("");
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);

  const trimmed = draft.trim();
  const draftHasText = trimmed.length > 0;

  // Auto-commit-on-type: every keystroke schedules a debounced save. The
  // first save in a typing burst calls onAdd; further saves while the user
  // keeps typing call onEdit on the same fact. The input STAYS populated
  // through saves — the debounce is just the save trigger, not an automatic
  // newline. Pressing Enter (or blurring the input) is the explicit
  // "finish this fact, start a new one" gesture: it flushes pending work,
  // clears the input, and resets the draft id so the next keystroke starts
  // a brand-new fact.
  //
  // The active draft id is held in TWO places that we keep synchronized:
  //   - `draftId` state    → drives rendering (we hide the corresponding row
  //                          from the rendered facts list while it's the
  //                          draft, since the input IS the visual row).
  //   - `draftIdRef`       → read by commit() / settled handlers without
  //                          relying on React state propagation timing.
  // Both are written together via `setDraftIdSynced`.
  type DraftId = { tempId: string; realId: string | null };
  const [draftId, setDraftIdState] = React.useState<DraftId | null>(null);
  const draftIdRef = React.useRef<DraftId | null>(null);
  const setDraftIdSynced = React.useCallback((value: DraftId | null) => {
    draftIdRef.current = value;
    setDraftIdState(value);
  }, []);

  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const draftRef = React.useRef(draft);
  React.useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  // Keep callback refs current so the debounced fire always uses the latest
  // handler closures.
  const onAddRef = React.useRef(onAdd);
  const onEditRef = React.useRef(onEdit);
  const onRemoveRef = React.useRef(onRemove);
  React.useEffect(() => {
    onAddRef.current = onAdd;
  }, [onAdd]);
  React.useEffect(() => {
    onEditRef.current = onEdit;
  }, [onEdit]);
  React.useEffect(() => {
    onRemoveRef.current = onRemove;
  }, [onRemove]);

  // The text last actually sent to the server (via onAdd/onEdit) for the active
  // draft. A commit with identical text -- e.g. the debounce auto-committed, then
  // Enter/blur flushes again with no change -- must NOT re-send an edit, which
  // would schedule a redundant second regeneration for one fact. Reset when the
  // draft is cleared so the next fact starts fresh.
  const lastCommittedTextRef = React.useRef<string | null>(null);

  const commit = React.useCallback(() => {
    const text = draftRef.current.trim();
    if (!text) {
      // Backspaced to empty after the draft was already saved → remove it.
      // If we never saved (draftId null), this is a no-op.
      const current = draftIdRef.current;
      if (current) {
        const idForRemove = current.realId ?? current.tempId;
        onRemoveRef.current(idForRemove);
        setDraftIdSynced(null);
        lastCommittedTextRef.current = null;
      }
      return;
    }
    const current = draftIdRef.current;
    if (current == null) {
      // First save in this typing burst — create the fact.
      const { tempId, settled } = onAddRef.current(text);
      if (!tempId) return; // no-op handle (e.g. messageId missing)
      lastCommittedTextRef.current = text;
      const entry: DraftId = { tempId, realId: null };
      setDraftIdSynced(entry);
      // When the server returns a realId, promote the ref so subsequent
      // edits go through the realId path in the hook. We only update the
      // entry if it's still the active draft (the user might have hit Enter
      // in the meantime, starting a fresh draft).
      void settled.then(({ realId }) => {
        if (draftIdRef.current === entry) {
          if (realId) {
            setDraftIdSynced({ tempId, realId });
          } else {
            setDraftIdSynced(null);
          }
        }
      });
      return;
    }
    // Subsequent save in the same burst — edit the existing draft, but ONLY if
    // the text actually changed since the last commit. Without this guard the
    // debounced auto-commit and a following Enter/blur fire two commits for one
    // unchanged fact, scheduling a redundant second regeneration.
    if (onEditRef.current && text !== lastCommittedTextRef.current) {
      const currentId = current.realId ?? current.tempId;
      void onEditRef.current(currentId, text);
      lastCommittedTextRef.current = text;
    }
  }, [setDraftIdSynced]);

  const clearDebounce = React.useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const scheduleCommit = React.useCallback(() => {
    clearDebounce();
    debounceTimerRef.current = setTimeout(() => {
      debounceTimerRef.current = null;
      commit();
    }, COMMIT_DEBOUNCE_MS);
  }, [clearDebounce, commit]);

  // Enter / blur: flush any pending commit, then clear the input and reset
  // the draft id so the next keystroke starts a brand-new fact. The just-
  // committed fact stops being the "draft" at this point — it materializes
  // as a row in the rendered list.
  const flushAndStartNewFact = React.useCallback(() => {
    clearDebounce();
    commit();
    setDraftIdSynced(null);
    setDraft("");
    lastCommittedTextRef.current = null;
  }, [clearDebounce, commit, setDraftIdSynced]);

  React.useEffect(() => {
    // Tear down any pending timer when the component unmounts so we don't
    // fire a commit against a stale handler ref.
    return () => clearDebounce();
  }, [clearDebounce]);

  // While there's an active draft (input populated and saved), suppress its
  // row from the rendered list — the input itself is the visual row, so
  // showing it again below would look like a duplicate.
  const visibleFacts = React.useMemo(() => {
    if (!draftId) return facts;
    return facts.filter((f) => {
      return f.id !== draftId.tempId && f.id !== draftId.realId;
    });
  }, [facts, draftId]);

  // Notify parent of the live pending count so it can show "+1" in CTAs as
  // the user types — Enter not required. The pending fact only counts when
  // the user has typed something AND it has not yet been saved to a patch
  // (draftId == null). Once the auto-commit fires, the draft is represented
  // by an entry in `facts` (the optimistic patch), so counting it through
  // both `pending` and `facts` would briefly show +2 between save and the
  // input being cleared by Enter / blur.
  React.useEffect(() => {
    const pending = draftHasText && draftId == null ? 1 : 0;
    onPendingCountChange?.(pending);
  }, [draftHasText, draftId, onPendingCountChange]);
  // An unmounting notebook takes its draft with it: report 0 so the host's
  // gate does not keep counting a draft that no longer exists.
  const onPendingCountChangeRef = React.useRef(onPendingCountChange);
  onPendingCountChangeRef.current = onPendingCountChange;
  React.useEffect(() => () => onPendingCountChangeRef.current?.(0), []);

  // Entrance-shine bookkeeping (#433): remember which rows existed at mount so
  // only facts that arrive afterwards (typed, or pushed in by the assistant
  // from chat) get the one-off gradient shine. Keyed by stableKey so a row's
  // temp->real id swap does not re-trigger it.
  const seenFactKeysRef = React.useRef<Set<string> | null>(null);
  const initialRenderDoneRef = React.useRef(false);
  if (seenFactKeysRef.current === null) {
    seenFactKeysRef.current = new Set(visibleFacts.map((f) => f.stableKey ?? f.id));
  }
  React.useEffect(() => {
    for (const f of visibleFacts) seenFactKeysRef.current?.add(f.stableKey ?? f.id);
    initialRenderDoneRef.current = true;
  });

  const { t } = useTranslation();

  return (
    <div
      role="group"
      aria-label={t("sourceReview.ownFacts")}
      className={`space-y-2${hideHeader || hideDivider ? "" : " border-t border-muted pt-3"}${className ? " " + className : ""}`}
    >
      {!hideHeader && (
        <div className="text-xs font-medium text-muted-foreground">
          {t("sourceReview.ownFacts")}
        </div>
      )}
      <ul role="list" className="space-y-1.5">
        {visibleFacts.map((fact) => {
          // stableKey survives the temp→real id transition AND the cleanup
          // pass, so using it as the React key keeps the textarea focused
          // mid-edit when the server confirms. The row's canonical id (used
          // for mutations) flips from tempId to realId silently behind it.
          const rowKey = fact.stableKey ?? fact.id;
          const isNewRow =
            initialRenderDoneRef.current && !(seenFactKeysRef.current?.has(rowKey) ?? true);
          const insertKey = insertAfterTarget?.key ?? null;
          const matchesInsertAnchor =
            insertKey !== null &&
            (insertKey === rowKey || insertKey === fact.id);
          return (
            <React.Fragment key={rowKey}>
              <FactRow
                fact={fact}
                dismissed={dismissedFactIds?.has(fact.id) ?? false}
                suggested={suggestedFactIds?.has(fact.id) ?? false}
                isNew={isNewRow}
                onToggle={onToggle ?? onRemove}
                onRemove={onRemove}
                onEdit={onEdit}
                onDraftChange={onDraftChange}
                onInsertAfter={
                  onAddAfter
                    ? (initialText) =>
                        setInsertAfterTarget({ key: rowKey, initialText: initialText ?? "" })
                    : undefined
                }
                onFactHover={onFactHover}
                highlighted={highlightedFactIds?.has(fact.id) ?? false}
              />
              {onAddAfter && matchesInsertAnchor && (
                <InsertAfterDraftRow
                  initialText={insertAfterTarget?.initialText ?? ""}
                  onDraftChange={onDraftChange}
                  onCommit={(text, viaEnter, afterText) => {
                    // Pass the stableKey as the anchor — the hook's splice
                    // logic matches both stableKey and id, but stableKey is
                    // the one that survives subsequent id transitions on
                    // this row, so a chained insert stays anchored even
                    // after the row's own server confirmation lands.
                    const { tempId } = onAddAfter(rowKey, text);
                    setInsertAfterTarget(
                      viaEnter && tempId
                        ? { key: tempId, initialText: afterText ?? "" }
                        : null,
                    );
                  }}
                  onCancel={() => setInsertAfterTarget(null)}
                />
              )}
            </React.Fragment>
          );
        })}
        <li className="flex items-start gap-2">
          {/* The "checkbox" next to the input mirrors the included state of a
              real fact row — when the user has typed anything, the box shows
              a check (the draft will become a fact with that check on). When
              the input is empty, an empty box. */}
          <span
            aria-hidden="true"
            className={`shrink-0 mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-[4px] border bg-background transition-colors ${
              draftHasText
                ? "border-primary-accent/60 text-primary-accent"
                : "border-input"
            }`}
          >
            {draftHasText && <Check className="h-3 w-3" strokeWidth={3} />}
          </span>
          <textarea
            ref={inputRef}
            rows={1}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              onDraftChange?.();
              e.target.style.height = "auto";
              e.target.style.height = e.target.scrollHeight + "px";
              if (e.target.value.trim() === "") {
                // Emptied: drop the saved draft right away so the CTA gate
                // doesn't keep counting it for the debounce window.
                clearDebounce();
                draftRef.current = e.target.value;
                commit();
              } else {
                scheduleCommit();
              }
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                const el = e.currentTarget;
                const pos = el.selectionStart ?? draft.length;
                const after = draft.slice(pos);
                // Caret at end (or nothing after it): finish this fact and
                // start an empty new one, as before.
                if (after.trim() === "") {
                  flushAndStartNewFact();
                  return;
                }
                // Split at the caret: commit the text before it as the
                // finished fact, then carry the rest of the line down into a
                // brand-new draft — the WYSIWYG "split the bullet" behavior.
                const before = draft.slice(0, pos);
                clearDebounce();
                draftRef.current = before;
                commit();
                setDraftIdSynced(null);
                draftRef.current = after;
                setDraft(after);
                // Restore the caret to the start of the moved text once React
                // has repainted the textarea with the shorter value.
                requestAnimationFrame(() => {
                  const node = inputRef.current;
                  if (node) {
                    node.selectionStart = 0;
                    node.selectionEnd = 0;
                    node.style.height = "auto";
                    node.style.height = node.scrollHeight + "px";
                  }
                });
              }
            }}
            onBlur={flushAndStartNewFact}
            placeholder={t("sourceReview.addNewFact")}
            aria-label={t("sourceReview.addNewFactAria")}
            className="flex-1 resize-none overflow-hidden border-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-0"
          />
        </li>
      </ul>
    </div>
  );
}

/**
 * The "Allgemein" / "Allgemeine Notizen" section: heading above a
 * {@link GeneralSectionCard} holding the ticket-level {@link FactsNotebook}
 * (facts tied to no single customer request). Venus and Neptune render this
 * identically except for the sources block below the notebook — Venus lists a
 * flat/orphan reference list, Neptune reuses its own to-do reference panel —
 * so that part stays a `children` slot the host controls.
 */
export function GeneralNotesSection({
  title,
  notebook,
  beforeNotebook,
  children,
  headingProps,
  confirmation,
  appearance = "v1",
}: {
  /** The section headline, e.g. `t("sourceReview.generalNotes")`. */
  title: string;
  notebook: Omit<FactsNotebookProps, "hideHeader">;
  /** Rendered inside the card above the notebook — e.g. Venus's transient
   *  "just confirmed via chat" placeholder rows. Omit when the host has none. */
  beforeNotebook?: React.ReactNode;
  /** The sources block rendered below the notebook, e.g. the unattributed/orphan
   *  references. Each host supplies its own reference-list component here. */
  children?: React.ReactNode;
  /** Extra props (e.g. `data-general-facts-section`) spread onto the heading row. */
  headingProps?: React.HTMLAttributes<HTMLDivElement> & {
    [dataAttr: `data-${string}`]: string | boolean | undefined;
  };
  /** #820: the confirm control for the notes. Omit while there is nothing to confirm. */
  confirmation?: GeneralNotesConfirmation;
  /** The card design. Default `"v1"`. */
  appearance?: SourceReviewAppearance;
}) {
  const confirmControl = confirmation && (
    <ConfirmChangesButton
      dirty={confirmation.dirty}
      onConfirm={confirmation.onConfirm}
      onUnconfirm={confirmation.onUnconfirm}
      tutorial={confirmation.tutorial}
    />
  );
  if (appearance === "v2") {
    // The data anchor the host scrolls to rides on the section, as the design library has it.
    return (
      <V2GeneralNotesSection title={title} {...(headingProps as React.HTMLAttributes<HTMLElement>)}>
        {beforeNotebook}
        <FactsNotebook {...notebook} hideHeader />
        {children}
        {confirmControl}
      </V2GeneralNotesSection>
    );
  }
  return (
    <div>
      <div
        {...headingProps}
        className={`flex items-baseline gap-1.5 py-1.5 text-sm font-medium${
          headingProps?.className ? ` ${headingProps.className}` : ""
        }`}
      >
        <span>{title}</span>
      </div>
      <GeneralSectionCard>
        {beforeNotebook}
        <FactsNotebook {...notebook} hideHeader />
        {children}
        {confirmControl}
      </GeneralSectionCard>
    </div>
  );
}

interface FactRowProps {
  fact: MessageFact;
  dismissed: boolean;
  /** Deselected by the AI, not by an agent (#706). Only meaningful with
   *  `dismissed`. */
  suggested?: boolean;
  /** True when this row appeared after the notebook's initial render (the
   *  agent typed it, or the assistant pushed it in from chat). Drives the
   *  one-off fade-in entrance; existing rows never animate. */
  isNew?: boolean;
  onToggle: (id: string) => void;
  /** Permanent removal. Now invoked only from the inline "edit-to-empty"
   *  flow (clear the textarea and press Enter / blur) — there is no longer
   *  a hover-x delete button. */
  onRemove: (id: string) => void;
  onEdit?: (id: string, content: string) => void | Promise<void>;
  /** Fired on every keystroke while editing, before the debounced commit on
   *  blur/Enter. Lets the parent show a "regenerating" indicator immediately
   *  as the agent types, rather than only after the mutation actually fires. */
  onDraftChange?: () => void;
  /** Fired after a successful Enter commit (non-empty content). Lets the
   *  parent render an editable draft row directly after this fact so the
   *  agent can keep adding facts without losing position. Blur commits do
   *  NOT fire this — only the explicit Enter chord. The parent already
   *  has the fact id from its render closure. `initialText` carries the "rest
   *  of the line" when the user presses Enter mid-fact, so the injected draft
   *  row starts pre-filled with the text after the caret (WYSIWYG split). */
  onInsertAfter?: (initialText?: string) => void;
  /** Propagates hover events to the answer text highlight system. */
  onReferenceHover?: (id: string | null) => void;
  /** #500: fires with this fact's id while the pointer rests on the row, and
   *  `null` when it leaves. The host resolves the id to the fact's grounding
   *  sources (`factSourceReferenceIds`) and highlights those cards, so the agent
   *  can see where an AI bullet came from. Facts without provenance still fire —
   *  resolving them simply yields no cards and nothing highlights. */
  onFactHover?: (factId: string | null) => void;
  /** True while the pointer rests on a source this fact was drawn from, so
   *  hovering a source highlights the facts written from it. */
  highlighted?: boolean;
}

// onReferenceHover is intentionally not destructured. Callers still pass it (the
// plumbing stays on the interface), but the reference-number badge that consumed
// it has been removed, so the row no longer renders any number. (onFactHover is separate: it
// reports the FACT's id for the fact -> source highlight, not a reference id.)
// isNew is intentionally not destructured: the chat-added entrance is now
// orchestrated by the host (ProposedAnswerTab) via data-fact-id (hide the row,
// then reveal it as the fly pill lands), so the row itself no longer animates.
function FactRow({ fact, dismissed, suggested = false, onToggle, onRemove, onEdit, onDraftChange, onInsertAfter, onFactHover, highlighted }: FactRowProps) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(fact.content);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const aiSuggested = dismissed && suggested;
  const machineScope = React.useMemo(
    () => factMachineScope(fact.sourceRefs),
    [fact.sourceRefs],
  );

  // Sync draft from fact.content ONLY when fact.content actually changes
  // (external edit landed, server rejected our optimistic update, etc.).
  // Do NOT re-sync on the editing → not-editing transition: between
  // `setIsEditing(false)` and the optimistic cache mutation propagating
  // forward there's a render window where fact.content still holds the
  // pre-edit value. Re-syncing draft to that stale value during that
  // window is exactly what produced the brief flash to the old text
  // after pressing Enter. By gating on a content-changed check we keep
  // the user's just-committed text on screen until the cache catches up.
  const prevFactContent = React.useRef(fact.content);
  React.useEffect(() => {
    if (!isEditing && fact.content !== prevFactContent.current) {
      setDraft(fact.content);
    }
    prevFactContent.current = fact.content;
  }, [fact.content, isEditing]);

  React.useEffect(() => {
    if (isEditing && inputRef.current) {
      const el = inputRef.current;
      el.style.height = "auto";
      el.style.height = el.scrollHeight + "px";
      el.focus();
      el.select();
    }
  }, [isEditing]);

  const commit = (viaEnter: boolean) => {
    const trimmed = draft.trim();
    // Empty edit → remove the fact. Do NOT setIsEditing(false) or restore
    // draft to fact.content first — both would force one extra render that
    // either repaints the just-cleared text or briefly shows the still-empty
    // span before the optimistic delete propagates and the parent unmounts
    // this row. Going straight to onRemove keeps the textarea visible (with
    // its empty content) for the single tick before unmount, so the user
    // sees no flash of restored or empty text. No insert-after chain on
    // empty commits — the user just deleted the row, not asked for another.
    if (!trimmed) {
      onRemove(fact.id);
      return;
    }
    setIsEditing(false);
    if (trimmed !== fact.content) {
      if (onEdit) {
        // Optimistically keep draft as the visible text. The useEffect above
        // will sync if the server response differs (e.g. trimming, rejection).
        void onEdit(fact.id, trimmed);
      } else {
        setDraft(fact.content);
      }
    } else {
      // No actual change — keep displayed draft in sync with fact.content.
      setDraft(fact.content);
    }
    // Enter chains an insert-after; blur does not. The parent reads this as
    // "render an editable draft row directly below this fact" so the agent
    // can keep adding facts in place without scrolling to the bottom input.
    if (viaEnter) {
      onInsertAfter?.();
    }
  };

  // Enter pressed mid-fact: keep the text before the caret in this fact and
  // carry the rest down into the new bullet below — the WYSIWYG split. Only
  // reached when both sides are non-empty; the caret-at-end / caret-at-start
  // cases fall back to the plain commit(true) above.
  const commitSplit = (before: string, after: string) => {
    const beforeTrimmed = before.trim();
    setIsEditing(false);
    if (beforeTrimmed !== fact.content) {
      if (onEdit) {
        setDraft(beforeTrimmed);
        void onEdit(fact.id, beforeTrimmed);
      } else {
        setDraft(fact.content);
      }
    } else {
      setDraft(fact.content);
    }
    onInsertAfter?.(after);
  };

  const cancel = () => {
    setDraft(fact.content);
    setIsEditing(false);
  };

  return (
    <li
      // data-fact-id lets the host (ProposedAnswerTab) locate this row's landing
      // spot to animate a fact "flying" from the chat into the list (#433).
      data-fact-id={fact.id}
      role="listitem"
      aria-label={`${t("sourceReview.ownFactInfo")}: ${fact.content}`}
      // No CSS entrance animation here: for a chat-added fact the host
      // (ProposedAnswerTab) hides this row and reveals it as the "fly" pill
      // lands (#433). A CSS fade-in would fight that host-controlled opacity.
      className={`group flex items-start gap-2 text-sm rounded-md -mx-1 px-1 transition-colors${highlighted ? " bg-primary/5 ring-1 ring-primary/30" : ""}`}
      // #500: hovering the row highlights the sources this fact was drawn from.
      // On the <li> rather than the text span so the whole row is the target —
      // the agent's pointer lands anywhere along the bullet, not just on glyphs.
      onMouseEnter={onFactHover ? () => onFactHover(fact.id) : undefined}
      onMouseLeave={onFactHover ? () => onFactHover(null) : undefined}
    >
      <button
        type="button"
        onClick={() => onToggle(fact.id)}
        role="checkbox"
        aria-checked={!dismissed}
        aria-label={dismissed ? t("sourceReview.includeFact") : t("sourceReview.excludeFact")}
        title={
          aiSuggested
            ? t("sourceReview.suggestedFact")
            : dismissed
              ? t("sourceReview.includeSource")
              : t("sourceReview.excludeSource")
        }
        className={`shrink-0 mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-[4px] border bg-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-accent/40 focus-visible:ring-offset-1 ${
          aiSuggested
            ? "border-transparent hover:opacity-80"
            : dismissed
              ? "border-input text-muted-foreground hover:border-primary-accent/60"
              : "border-primary-accent/60 text-primary-accent hover:border-primary-accent"
        }`}
        // #706: an AI-offered bullet gets the brand gradient (Purple → Cyan →
        // Yellow, DESIGN.md's marker for AI content) as its checkbox border —
        // padding-box paints the inside, border-box shows through the
        // transparent border.
        style={
          aiSuggested
            ? {
                background:
                  "linear-gradient(var(--background), var(--background)) padding-box, linear-gradient(to bottom right, var(--gradient-from), var(--gradient-via), var(--gradient-to)) border-box",
              }
            : undefined
        }
      >
        {!dismissed && <Check className="h-3 w-3" strokeWidth={3} />}
      </button>
      {isEditing && onEdit ? (
        <FactEditTextarea
          inputRef={inputRef}
          value={draft}
          onValueChange={(next) => {
            setDraft(next);
            onDraftChange?.();
          }}
          onCommit={commit}
          onSplit={commitSplit}
          onCancel={cancel}
          ariaLabel={t("sourceReview.editFact")}
        />
      ) : (
        // Display `draft` rather than `fact.content` so the text doesn't
        // briefly flash back to the pre-edit value between
        // setIsEditing(false) and the mutation's optimistic cache update
        // propagating fact.content forward. The useEffect above keeps
        // draft <- fact.content in sync whenever we're not editing.
        //
        // Dismissed rows stay editable — the line-through visual signals the
        // dismissal but the agent can still click to refine the wording. The
        // dismissed state itself only toggles via the checkbox. Only an agent's
        // deselection is struck through: a bullet the AI offered deselected
        // (#706) was never rejected, so it is just faded.
        <span
          // Landing anchor for the host's fact-fly animation (#433): the pill
          // aligns its own text to this span's exact position.
          data-fact-text
          className={`flex-1 whitespace-pre-wrap break-words transition-colors ${
            aiSuggested
              ? "text-muted-foreground"
              : dismissed
              ? "text-muted-foreground line-through"
              // AI provenance is now signalled by the sparkle icon (#433), so
              // the text stays upright and full-contrast — no more grey italics.
              : "text-foreground"
          }${onEdit ? " cursor-text" : ""}`}
          onClick={() => {
            if (onEdit) setIsEditing(true);
          }}
          title={
            fact.isAiGenerated
              ? t("sourceReview.aiBulletEditHint")
              : onEdit
                ? t("sourceReview.edit")
                : undefined
          }
        >
          {draft}
        </span>
      )}
      {/* Machine scope: sits at the right edge next to the AI sparkle, so a fact
          written entirely from similar-type sources reads as one glance-level
          exception. Rendered for human-typed facts too when their provenance says
          so — the caveat is about the evidence, not about who wrote the line. */}
      {machineScope && (
        <MachineScopeMarker
          expectedLabel={machineScope.expectedLabel}
          sourceLabel={machineScope.sourceLabel}
          factLabel={machineScope.factLabel}
          className="mt-0.5"
        />
      )}
      {fact.isAiGenerated && (
        // #433: the single marker for an AI/chat-added fact — a brand-gradient
        // sparkle at the RIGHT end of the row (the flex-1 text above pushes it
        // there). Checkbox stays on the left so the fact can be un/re-checked.
        <GradientSparkle className="mt-0.5 h-[18px] w-[18px] shrink-0" />
      )}
    </li>
  );
}

interface InsertAfterDraftRowProps {
  /** Fires once the user commits the typed content (Enter or blur). The
   *  `viaEnter` flag tells the parent whether to chain another draft below
   *  the just-committed row. The draft row owns its own textarea state and
   *  unmounts after commit, so the parent doesn't need to clear anything. */
  onCommit: (text: string, viaEnter: boolean, afterText?: string) => void;
  /** Fires when the user abandons the draft — empty commit (Enter / blur),
   *  or Escape. The parent should hide the draft row. */
  onCancel: () => void;
  /** Text to pre-fill the draft with — the "rest of the line" carried down
   *  when the user split an existing fact with Enter. Defaults to empty. */
  initialText?: string;
  /** Fired on every keystroke, before the debounced commit on blur/Enter. */
  onDraftChange?: () => void;
}

/**
 * The auto-growing inline fact editor shared by FactRow's edit mode and
 * InsertAfterDraftRow: single-row textarea that grows with content, commits
 * on blur, and on Enter either commits (caret at start/end) or splits the
 * line at the caret (mid-line) — Escape cancels.
 */
function FactEditTextarea({
  inputRef,
  value,
  onValueChange,
  onCommit,
  onSplit,
  onCancel,
  ariaLabel,
}: {
  inputRef: React.Ref<HTMLTextAreaElement>;
  value: string;
  onValueChange: (next: string) => void;
  /** Enter with the caret at start/end, or blur (viaEnter=false). */
  onCommit: (viaEnter: boolean) => void;
  /** Enter mid-line: commit `before`, carry `after` down into the next row. */
  onSplit: (before: string, after: string) => void;
  onCancel: () => void;
  ariaLabel: string;
}) {
  return (
    <textarea
      ref={inputRef}
      rows={1}
      value={value}
      onChange={(e) => {
        onValueChange(e.target.value);
        e.target.style.height = "auto";
        e.target.style.height = e.target.scrollHeight + "px";
      }}
      onBlur={() => onCommit(false)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          const el = e.currentTarget;
          const pos = el.selectionStart ?? value.length;
          const after = value.slice(pos);
          const before = value.slice(0, pos);
          // Caret at end / start: plain commit. Mid-line: commit `before`,
          // carry `after` down into the next row.
          if (after.trim() === "" || before.trim() === "") {
            onCommit(true);
          } else {
            onSplit(before, after);
          }
        } else if (e.key === "Escape") {
          e.preventDefault();
          onCancel();
        }
      }}
      aria-label={ariaLabel}
      className="flex-1 resize-none overflow-hidden border-0 bg-transparent text-sm text-foreground focus-visible:outline-none focus-visible:ring-0"
    />
  );
}

/**
 * Local, ephemeral draft row injected directly below an existing fact when
 * the user presses Enter inside that fact's inline edit. Only materializes
 * as a real pending fact (via `onAddAfter`) when the user commits non-empty
 * content — empty commits and blurs simply cancel, so abandoned inserts do
 * not leak as empty rows on the server.
 */
function InsertAfterDraftRow({ onCommit, onCancel, initialText = "", onDraftChange }: InsertAfterDraftRowProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = React.useState(initialText);
  const inputRef = React.useRef<HTMLTextAreaElement | null>(null);
  const trimmed = draft.trim();
  const hasText = trimmed.length > 0;

  React.useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
    el.focus();
    // Caret at the start of any pre-filled (moved) text so the user keeps
    // typing from where they split the line above, not at the end of it.
    el.selectionStart = 0;
    el.selectionEnd = 0;
  }, []);

  const commit = (viaEnter: boolean) => {
    if (!hasText) {
      onCancel();
      return;
    }
    onCommit(trimmed, viaEnter);
  };

  return (
    <li role="listitem" className="group flex items-start gap-2 text-sm">
      <span
        aria-hidden="true"
        className={`shrink-0 mt-0.5 inline-flex h-4 w-4 items-center justify-center rounded-[4px] border bg-background transition-colors ${
          hasText ? "border-primary-accent/60 text-primary-accent" : "border-input"
        }`}
      >
        {hasText && <Check className="h-3 w-3" strokeWidth={3} />}
      </span>
      <FactEditTextarea
        inputRef={inputRef}
        value={draft}
        onValueChange={(next) => {
          setDraft(next);
          onDraftChange?.();
        }}
        onCommit={commit}
        // Mid-line Enter: commit `before`, carry `after` down into the next
        // chained row.
        onSplit={(before, after) => onCommit(before.trim(), true, after)}
        onCancel={onCancel}
        ariaLabel={t("sourceReview.insertFact")}
      />
    </li>
  );
}
